import React from 'react';
import {Listbox} from '@headlessui/react';
import UIStdSelectButton from '../UIStdSelectButton';
import {IOptGroup, IOption, OptionSelectedState} from '../UIOption';
import UIMultiSelectOptionList from './UIMultiSelectOptionList';

/**
 * @param preselected {boolean} If true, the first option from the list of options is selected
 * @param emptyLabel {string} Label to show when no option is selected
 */
type MultiSelectListBoxProps = {
  options: (IOption<boolean> | IOptGroup<boolean>)[]
  onChange: (values: string[]) => void
  containerClassName?: string
  btnClassName?: string
  disabled?: boolean
  value?: string
  preselected?: boolean
  emptyLabel?: string
  as?: 'div' | 'span'
}

function isOptGroup(value: IOption<boolean> | IOptGroup<boolean>): value is IOptGroup<boolean> {
  return (value as IOptGroup<boolean>).options !== undefined;
}

export default function UIMultiSelectListBox({
  containerClassName,
  disabled = false,
  onChange,
  options,
  emptyLabel = 'Select an item',
  as = 'div',
}: MultiSelectListBoxProps) {
  const selected = options
    .map((x) => (isOptGroup(x) ? x.options : [x]))
    .flat(1)
    .filter((x) => x.selected);

  const toOptions = (group: IOptGroup<boolean>) => {
    const children: IOption<OptionSelectedState>[] = group.options.map((x) => ({
      ...x,
      parent: false,
      selected: x.selected ? OptionSelectedState.YES : OptionSelectedState.NO,
    }));
    const selectedChildren = children.filter((x) => x.selected === OptionSelectedState.YES);
    let state = OptionSelectedState.NO;
    if (selectedChildren.length > 0) {
      state = selectedChildren.length === children.length ? OptionSelectedState.YES : OptionSelectedState.PARTIALLY;
    }
    const parent: IOption<OptionSelectedState> = {
      id: group.id, label: group.label, value: group.id, selected: state, parent: true,
    };
    return [parent].concat(children);
  };

  const onClick = (option: IOption<OptionSelectedState>) => {
    let selectedValues: string[] = [];
    if (!option.parent) {
      const onlyIds = selected.map((x) => x.id);
      if (option.selected === OptionSelectedState.YES) { // previous state
        selectedValues = onlyIds.filter((x) => x !== option.id);
      } else {
        selectedValues = onlyIds.concat([option.id]);
      }
    } else {
      const categoryTypeIds = options
        .filter((x) => x.id === option.id)
        .map((x) => (isOptGroup(x) ? x.options.map((o) => o.id) : []))
        .flat(1);
      const onlyIds = selected.map((x) => x.id);
      if (option.selected === OptionSelectedState.NO) { // previous state
        selectedValues = [...new Set(onlyIds.concat(categoryTypeIds))];
      } else {
        selectedValues = onlyIds.filter((x) => categoryTypeIds.indexOf(x) < 0);
      }
    }
    onChange(selectedValues);
  };

  const getLabel = () => {
    switch (selected.length) {
      case 0:
        return <span className="text-gray">{emptyLabel}</span>;
      case 1:
        return '1 type selected';
      default:
        return `${selected.length} types selected`;
    }
  };

  const items: IOption<OptionSelectedState>[] = options.map((x) =>
    (isOptGroup(x) ? toOptions(x) : [{
      ...x,
      selected: x.selected ? OptionSelectedState.YES : OptionSelectedState.NO,
    }])).flat(1);

  return (
    <Listbox value={undefined} onChange={onChange} as={as} className={containerClassName} disabled={disabled}>
      {({open}) => (
        <div className="relative">
          <UIStdSelectButton value={getLabel()} disabled={disabled}/>
          <UIMultiSelectOptionList options={items} open={open} onClick={onClick}/>
        </div>
      )}
    </Listbox>
  );
}
